import React from 'react';

import { DropdownSingle } from 'src/app/components/lib/dropdown';
import { FormSection } from 'src/app/components/lib/form';
import { Input } from 'src/app/components/lib/input';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Select } from 'src/app/components/lib/select';
import { WizardCard } from 'src/app/components/lib/wizard/card';
import { Features } from 'src/app/state/types/loc-profile';
import { useFeatureCheck } from 'src/app/state/user-dispensaries';

import { CalculationMethods } from '../../../models';
import { useDiscountWizardStateContext } from '../../state';
import { ConfigureStepActions } from '../../state/configure';
import { ApplicationMethodTypeId } from '../../types/index';

import { applicationMethodNames, useApplicationMethods } from './use-application-methods';
import { useSpringBigOffers } from './use-springbig-offers';

export function ApplicationCard() {
  const {
    configureStepState: { applicationMethod, externalId, loyaltyPointsToRedeem },
    configureStepDispatch,
    isManualDiscount,
    rewardsStepState: { discountMethod },
  } = useDiscountWizardStateContext();

  const applicationMethods = useApplicationMethods();

  const sbEnabled = applicationMethods.includes(ApplicationMethodTypeId.BySpringBig);
  const { options: springBigOffers, isLoading: springBigOffersLoading } = useSpringBigOffers(sbEnabled);

  const showExternalLoyaltyFeatureEnabled = useFeatureCheck(Features.ShowExternalLoyalty);
  const leaflogixLoyaltyEnabled = useFeatureCheck(Features.ShowLeafLogixLoyalty);

  // conditional rendering
  const showLoyaltyPointsInput =
    applicationMethod === ApplicationMethodTypeId.DutchieLoyalty ||
    applicationMethod === ApplicationMethodTypeId.ExternalLoyalty ||
    (applicationMethod === ApplicationMethodTypeId.Manually && !!leaflogixLoyaltyEnabled);

  const showManualDiscountExternalIdInput =
    (isManualDiscount && showExternalLoyaltyFeatureEnabled) ||
    applicationMethod === ApplicationMethodTypeId.ExternalLoyalty;
  const showAlpineIQExternalIdInput = applicationMethod === ApplicationMethodTypeId.ByAlpineIQ;
  const showSpringBigFields = applicationMethod === ApplicationMethodTypeId.BySpringBig;

  // disabled application method to prevent users from changing the method is Loyalty Multiplier is selected as the discount method on the rewards step
  const isLoyaltyMultiplierSelected = discountMethod === CalculationMethods.LoyaltyMultiplier;

  return (
    <WizardCard
      description='Specify whether this discount will be applied automatically, manually by a budtender, by code or by loyalty.'
      title='How will it initiate'
    >
      <FormSection>
        <Select
          disabled={isLoyaltyMultiplierSelected}
          label='Application method:'
          labelPlacement='top'
          required
          value={applicationMethod}
          onChange={({ target }) => {
            configureStepDispatch({ type: ConfigureStepActions.SET_APPLICATION_METHOD, payload: target.value });
          }}
        >
          {applicationMethods.map((method) => (
            <MenuItem key={applicationMethodNames[method]} value={method}>
              {applicationMethodNames[method]}
            </MenuItem>
          ))}
        </Select>
        {showLoyaltyPointsInput && (
          <Input
            label='Loyalty points to redeem:'
            labelPlacement='top'
            required={applicationMethod === ApplicationMethodTypeId.DutchieLoyalty}
            tooltip='The amount of loyalty points that must be spent to redeem this discount.'
            type='number'
            useV2Tooltip
            value={loyaltyPointsToRedeem}
            onChange={({ target }) => {
              configureStepDispatch({
                type: ConfigureStepActions.SET_LOYALTY_POINTS_TO_REDEEM,
                payload: target.value === '' ? '' : Number(target.value),
              });
            }}
          />
        )}
        {showManualDiscountExternalIdInput && (
          <Input
            label='External ID:'
            labelPlacement='top'
            tooltip='The identifier for this discount from an external loyalty system.'
            useV2Tooltip
            value={externalId}
            onChange={({ target }) => {
              configureStepDispatch({
                type: ConfigureStepActions.SET_EXTERNAL_ID,
                payload: target.value,
              });
            }}
          />
        )}
        {showAlpineIQExternalIdInput && (
          <Input
            label='Alpine IQ discount ID:'
            labelPlacement='top'
            required
            value={externalId}
            onChange={({ target }) => {
              configureStepDispatch({
                type: ConfigureStepActions.SET_EXTERNAL_ID,
                payload: target.value,
              });
            }}
          />
        )}
        {showSpringBigFields && (
          <>
            <DropdownSingle
              helpText='Select offer/reward'
              label='Offer/reward:'
              labelPlacement='top'
              loading={springBigOffersLoading}
              options={springBigOffers}
              required
              setValue={(val) =>
                configureStepDispatch({
                  type: ConfigureStepActions.SET_EXTERNAL_ID,
                  payload: String(val.id),
                })
              }
              value={externalId}
            />
            <Input disabled label='SpringBig discount ID:' labelPlacement='top' value={externalId} />
          </>
        )}
      </FormSection>
    </WizardCard>
  );
}
